import clsx from 'clsx';
import { isNil, toNumber } from 'lodash';
import { companySummaryUrl, firmSummaryUrl } from 'common/config/urls';
import { ValueWithLinkViewer } from 'components';
import {
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FIRM_SUMMARY_SPREADSHEET_INDENTED_CLASSNAME,
} from 'pages/Firms/common/constants';
import { CreateRowsReturn, FirmSummaryCellData, FirmSummaryRow } from 'pages/Firms/hooks/useFirmSummarySheet';
import { getExchangeRateTooltip } from 'pages/Funds/utils';
import { generateColumnKey, getArrayValue, getObjectValue } from 'utilities';
import { CreateFundRowsParams } from './createFundRows.types';

const createFundRows = (params: CreateFundRowsParams): CreateRowsReturn => {
  const { firmSlug, fundsByCompany, isAllFundsSelected } = params;

  const fundRows = getArrayValue(
    fundsByCompany?.map(fund => {
      const {
        company_captable_currency: companyCaptableCurrency,
        company_id: companyId,
        company_name: companyName,
        company_slug: companySlug,
        exchange_rate_to_fund_currency: exchangeRateToFundCurrency,
        fund_currency: fundCurrency,
        fund_id: fundId,
        fund_name: fundName,
        funds_count: fundsCount,
        is_parent: isParent,
        measurement_date: measurementDate,
        parent_key: parentKey,
      } = getObjectValue(fund);

      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });

      let companyNameWithCurrency = companyName;
      const queryParams = `?date=${measurementDate}`;
      const companyUrl = firmSlug && companySlug ? `${companySummaryUrl(firmSlug, companySlug)}${queryParams}` : '';
      const fundUrl = firmSlug && fundId ? firmSummaryUrl(firmSlug, measurementDate, String(fundId)) : '';

      const exchangeRateTooltip = getExchangeRateTooltip(
        fundCurrency,
        companyCaptableCurrency,
        toNumber(exchangeRateToFundCurrency)
      );

      // Display company cap table currency when it differs from fund currency
      if (fundCurrency !== companyCaptableCurrency) {
        companyNameWithCurrency = `${companyName} (${companyCaptableCurrency})`;
      }

      const displayName = companyNameWithCurrency;
      const displayUrl = companyUrl;
      const displayExchangeRate = exchangeRateTooltip;

      return {
        alias: columnKey,
        className: clsx({
          [FIRM_SUMMARY_SPREADSHEET_INDENTED_CLASSNAME]: !isNil(parentKey) && isAllFundsSelected,
        }),
        data: {
          companyId,
          companySlug,
          entityName: companyName,
          fundsCount,
          fundUrl,
          isParent,
          parentGrossIRR: getObjectValue(fund).parent_gross_irr,
          parentKey,
          parentType: 'company',
        } as FirmSummaryCellData,
        gridType: 'string',
        hasSubRows: isParent && fundsCount > 1,
        parent: parentKey,
        readOnly: true,
        showSubRows: true,
        value: (
          <ValueWithLinkViewer
            exchangeRateMessage={displayExchangeRate || undefined}
            name={displayName}
            url={displayUrl}
          />
        ),
      } as FirmSummaryRow;
    })
  );

  return fundRows;
};

export default createFundRows;
